import React, {useContext, useState, useEffect} from 'react'

import {makeStyles} from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import InputLabel from '@material-ui/core/InputLabel'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Tooltip from '@material-ui/core/Tooltip'
import Box from '@material-ui/core/Box'
import Dialog from '@material-ui/core/Dialog'

import dayjs from 'dayjs'

import NotificationsIcon from '@material-ui/icons/Notifications'
import SaveIcon from '@material-ui/icons/Save'
import DeleteIcon from '@material-ui/icons/Delete'

import AlertDialog from '../../AlertDialog';
import PushCreate from '../../PushCreate';
import TrackItem from '../TrackItem';

import _firebaseContext from '../../../contexts/firebaseContext';
import _appContext from '../../../contexts/appContext';

export default function TrackInfo({planetId, localeId, track, handleDelete, setIsLoading}) {
    const [performingAction, setPerformingAction] = useState(true)
    const [name, setName] = useState(track.data.name || '')
    const [auraColor, setAuraColor] = useState(
        track.data.auraColor || '#e1bee7'
    )
    const [description, setDescription] = useState(track.data.description || '')
    const [nightDescription, setNightDescription] = useState(
        track.data.nightDescription || ''
    )

    const [trackUrl, setTrackUrl] = useState(track.data.trackUrl || '')
    const [nightTrackUrl, setNightTrackUrl] = useState(
        track.data.nightTrackUrl || ''
    )

    const [trackTimespan, setTrackTimespan] = useState(
        track.data.trackTimespan || '0'
    )
    const [nightTrackTimespan, setNightTrackTimespan] = useState(
        track.data.nightTrackTimespan || '0'
    )

    const [lastEditDate, setLastEditDate] = useState(track.data.lastEditDate)
    const [trackStatus, setTrackStatus] = useState(
        track.data.trackStatus || 'regular'
    )

    const [trackCreator, setTrackCreator] = useState('')
    const [lastEditAuthor, setLastEditAuthor] = useState('')

    const [trackFile, setTrackFile] = useState({})
    const [nightTrackFile, setNightTrackFile] = useState({})

    // const [errors, setErrors] = useState(false);
    const [alertDialog, setAlertDialog] = useState(false)
    const [changed, setChanged] = useState(false)
    const [changedData, setChangedData] = useState({})
    const [errors, setErrors] = useState({
        name: false,
        auraColor: false,
        description: false,
    })

    const [pushCreate, setPushCreate] = useState(false)

    const handleOpenPushCreate = (event) => {
        setPushCreate(true)
    }

    const handleClosePushCreate = () => {
        setPushCreate(false)
    }

    const firebaseContext = useContext(_firebaseContext)
    const appContext = useContext(_appContext)
    const classes = useStyles({auraColor})

    const audioElement = document.createElement('AUDIO')
    const nightAudioElement = document.createElement('AUDIO')

    useEffect(() => {
        updateTrackInfo()
    }, [
        track.data.lastEditDate,
        track.data.trackCreator,
        track.data.lastEditAuthor,
        track.data.trackUrl,
        track.data.nightTrackUrl,
    ])

    useEffect(() => {
        setChangedData({
            ...changedData,
            name,
        })
    }, [name])

    useEffect(() => {
        setChangedData({
            ...changedData,
            auraColor,
        })
    }, [auraColor])

    useEffect(() => {
        setChangedData({
            ...changedData,
            description,
            nightDescription,
        })
    }, [description, nightDescription])

    useEffect(() => {
        if (!(trackFile instanceof File)) return

        setChangedData({
            ...changedData,
            trackUrl: trackFile,
        })
    }, [trackFile])

    useEffect(() => {
        if (!(nightTrackFile instanceof File)) return

        setChangedData({
            ...changedData,
            nightTrackUrl: nightTrackFile,
        })
    }, [nightTrackFile])

    const getTrackTimestamRepresent = () =>
        `${parseInt(trackTimespan / 60)}:${parseInt(trackTimespan % 60)}`

    const getNightTrackTimestamRepresent = () =>
        `${parseInt(nightTrackTimespan / 60)}:${parseInt(
            nightTrackTimespan % 60
        )}`

    const updateTrackInfo = () => {
        if (track.data.trackCreator !== undefined)
            setTrackCreator(track.data.trackCreator.name)
        if (track.data.lastEditAuthor !== undefined)
            setLastEditAuthor(track.data.lastEditAuthor.name)
        if (track.data.lastEditDate !== undefined)
            setLastEditDate(track.data.lastEditDate)
        if (track.data.trackUrl !== undefined)
            setTrackUrl(track.data.trackUrl || '')
        if (track.data.nightTrackUrl !== undefined)
            setNightTrackUrl(track.data.nightTrackUrl || '')
    }

    const closeNativeColorPicker = (event) => {
        const newColor = event.target.value
        if (newColor === auraColor) return

        if (newColor !== null) {
            handleAuraColorChange(newColor)
        }
    }

    const handleNameChange = (event) => {
        const newName = event.target.value
        setChanged(true)
        setName(newName)
        if (newName.length === 0) {
            setErrors({
                ...errors,
                name: 'Пустое наименование',
            })
        } else {
            setErrors({
                ...errors,
                name: false,
            })
        }
        setChangedData({
            ...changedData,
            name,
        })
    }

    const handleAuraColorChange = (color) => {
        setChanged(true)
        setAuraColor(color)
        if (!/^#(?:[0-9a-f]{3}){1,2}$/i.test(color)) {
            setErrors({
                ...errors,
                auraColor: 'Некорректный цвет ауры',
            })
        } else {
            setErrors({
                ...errors,
                auraColor: false,
            })
        }

        setChangedData({
            ...changedData,
            auraColor: color,
        })
    }

    const handleDescriptionChange = (event) => {
        setChanged(true)
        setDescription(event.target.value)

        setChangedData({
            ...changedData,
            description: event.target.value,
        })
    }

    const handleNightDescriptionChange = (event) => {
        setChanged(true)
        setNightDescription(event.target.value)

        setChangedData({
            ...changedData,
            nightDescription: event.target.value,
        })
    }

    const handleDurationOfAudioFileUrl = (fileUrl, trackType) => {
        audioElement.src = fileUrl;

        if (trackType === 'day') {
            setTrackTimespan('0');
            audioElement.addEventListener('canplaythrough', () =>
                setTrackTimespan(audioElement.duration)
            )
        }
        if (trackType === 'night') {
            setNightTrackTimespan('0');
            audioElement.addEventListener('canplaythrough', () =>
                setNightTrackTimespan(audioElement.duration)
            )
        }
    }

    const handleTrackUrlChange = (event) => {
        console.log('track url change', event.target.value)
        setChanged(true)
        setTrackUrl(event.target.value)
        handleDurationOfAudioFileUrl(event.target.value, 'day');

        setChangedData({
            ...changedData,
            trackUrl: event.target.value,
        })
    }

    const handleNightTrackUrlChange = (event) => {
        console.log('track url change', event.target.value)
        setChanged(true)
        setNightTrackUrl(event.target.value)
        handleDurationOfAudioFileUrl(event.target.value, 'night');

        setChangedData({
            ...changedData,
            nightTrackUrl: event.target.value,
        })
    }

    const handleDurationOfAudioFile = (file) => {
        audioElement.src = URL.createObjectURL(file)
        audioElement.addEventListener('canplaythrough', () =>
            setTrackTimespan(audioElement.duration)
        )
    }

    const handleDurationOfNightAudioFile = (file) => {
        nightAudioElement.src = URL.createObjectURL(file)
        nightAudioElement.addEventListener('canplaythrough', () =>
            setNightTrackTimespan(nightAudioElement.duration)
        )
    }

    const currentAudioPlayer = new Audio()
    const currentNightAudioPlayer = new Audio()

    useEffect(() => {
        currentAudioPlayer.src = trackUrl
        currentNightAudioPlayer.src = nightTrackUrl
        return () => {
            currentAudioPlayer.remove()
            currentAudioPlayer.src = ''
            currentAudioPlayer.pause()

            currentNightAudioPlayer.remove()
            currentNightAudioPlayer.src = ''
            currentNightAudioPlayer.pause()
        }
    })

    const playCurrentAudio = () => {
        currentAudioPlayer.src = trackUrl
        currentAudioPlayer.addEventListener('canplaythrough', () =>
            currentAudioPlayer.play()
        )
    }

    const moveTrack = async (currentTrackUrl, currentDescription, currentSetTrackUrl) => {
        const newTrackUrl = await firebaseContext.moveTrackFile(currentTrackUrl, currentDescription)
        if (newTrackUrl) {
            currentSetTrackUrl(newTrackUrl)
            setChanged(true)
            appContext.snackbar.showSnackbarMsg('Перемещено')
        } else {
            appContext.snackbar.showSnackbarMsg('Произошла ошибка')
        }
    }

    const playCurrentNightAudio = () => {
        currentNightAudioPlayer.src = nightTrackUrl
        currentNightAudioPlayer.addEventListener('canplaythrough', () =>
            currentNightAudioPlayer.play()
        )
    }

    const pauseCurrentAudio = () => {
        currentAudioPlayer.pause()
    }

    const pauseCurrentNightAudio = () => {
        currentNightAudioPlayer.pause()
    }

    const handleAddTrackFile = async (event, trackType) => {
        const file = event.target.files[0]
        setChanged(true)

        setDescription(file.name)
        setChangedData({
            ...changedData,
            description: file.name,
        })

        setTrackFile(file)
        handleDurationOfAudioFile(file)
        await handleUpload(file, trackType);
    }

    const handleAddNightTrackFile = async (event, trackType) => {
        const file = event.target.files[0]

        setChanged(true)

        setNightDescription(file.name)
        setChangedData({
            ...changedData,
            nightDescription: file.name,
        })

        setNightTrackFile(file)
        handleDurationOfNightAudioFile(file)
        await handleUpload(file, trackType);
    }

    const handleLastEditDateChange = (event) => {
        setChanged(true)
        setLastEditDate(event.target.value)

        setChangedData({
            ...changedData,
            lastEditDate: event.target.value,
        })
    }

    const handleTrackStatusChange = (event) => {
        setChanged(true)
        setTrackStatus(event.target.value)

        setChangedData({
            ...changedData,
            trackStatus: event.target.value,
        })
    }

    const handleTrackCreatorChange = (event) => {
        setChanged(true)
        setTrackCreator(event.target.value)

        setChangedData({
            ...changedData,
            trackCreator: event.target.value,
        })
    }

    const handleLastEditAuthorChange = (event) => {
        setChanged(true)
        setLastEditAuthor(event.target.value)

        setChangedData({
            ...changedData,
            lastEditAuthor: event.target.value,
        })
    }

    const handleUpload = async (file, trackType) => {
        let trackFileUrl = undefined;

        if (file instanceof File && trackType === 'day') {
            trackFileUrl = await firebaseContext.uploadTrackFile(file);
        }
        if (file instanceof File && trackType === 'night') {
            trackFileUrl = await firebaseContext.uploadTrackFile(file);
        }

        if (file instanceof File && !trackFileUrl) {
            appContext.snackbar.showSnackbarMsg('Произошла ошибка');
            return;
        }

        if (!(file instanceof File) && trackType === 'day') {
            trackFileUrl = trackUrl;
        }
        if (!(file instanceof File) && trackType === 'night') {
            trackFileUrl = nightTrackUrl;
        }

        if (trackType === 'day') {
            setTrackUrl(trackFileUrl);
        }
        if (trackType === 'night') {
            setNightTrackUrl(trackFileUrl);
        }

        if (trackFileUrl) {
            appContext.snackbar.showSnackbarMsg('Трек успешно загружен');
        }
    }

    const getChangesDiff = (before, after) => {
        const difference = {}
        Object.keys(after).forEach(key => {
            if (before[key] !== after[key]) difference[key] = after[key]
        })
        return difference
    }

    const handleSave = async (event) => {
        if (Object.keys(changedData).length === 0) return
        if (Object.values(errors).some((field) => field)) return

        setIsLoading(true);
        for (let key in changedData) {
            if (typeof changedData[key] === 'string')
                changedData[key] = changedData[key].trim()
        }

        const result = await firebaseContext
            .updateTrack(
                planetId,
                track.id,
                localeId,
                changedData,
                trackUrl,
                trackTimespan,
                nightTrackUrl,
                nightTrackTimespan
            )
            .catch(() =>
                appContext.snackbar.showSnackbarMsg('Произошла ошибка')
            )

        if (result.error) {
            setErrors(true)
        }
        if (!result.error) {
            await firebaseContext.updateCache();

            changedData.nightTrackTimespan = nightTrackTimespan;
            changedData.trackTimespan = trackTimespan;
            await firebaseContext.updateHistory(planetId, track, 'onUpdate', getChangesDiff(track.data, changedData));
        }

        setTrackFile({})
        setNightTrackFile({})
        setChangedData({})
        setChanged(false)
        setIsLoading(false);

        appContext.snackbar.showSnackbarMsg(result.message)
    }

    const openAlertDialog = (event) => {
        setAlertDialog(true)
    }

    const closeAlertDialog = (deleteState) => {
        if (deleteState) {
            const result = handleDelete()

            if (result.error) {
                setErrors(true)
            }
        }

        setAlertDialog(false)
    }

    return (
        <div className={classes.tabContentWrapper}>
            {track === undefined ? (
                <Typography variant="h5">Нет данных по треку</Typography>
            ) : (
                <>
                    <TextField
                        autoFocus
                        disabled={!performingAction}
                        error={!!(errors && errors.name)}
                        helperText={errors.name || ''}
                        label="Наименование трека"
                        required
                        type="text"
                        value={name}
                        className={classes.textfield}
                        onChange={handleNameChange}
                    />
                    <div className={classes.auraColorWrapper}>
                        <TextField
                            disabled={!performingAction}
                            error={!!(errors && errors.auraColor)}
                            helperText={errors.auraColor || ''}
                            label="Аура трека"
                            required
                            type="text"
                            value={auraColor}
                            className={classes.textfield}
                            onChange={closeNativeColorPicker}
                        />
                        <label htmlFor="palette">
                            <input
                                style={{display: 'none'}}
                                id="palette"
                                name="palette"
                                type="color"
                                onChange={closeNativeColorPicker}
                                value={auraColor}
                            />
                            <Tooltip title="Открыть палитру" placement="top">
                                <Box
                                    boxShadow={3}
                                    className={classes.colorIndicator}
                                />
                            </Tooltip>
                        </label>
                    </div>

                    <TrackItem
                        performingAction={performingAction}
                        description={description}
                        handleDescriptionChange={handleDescriptionChange}
                        handleTrackUrlChange={handleTrackUrlChange}
                        handleAddTrackFile={handleAddTrackFile}
                        trackFile={trackFile}
                        moveTrack={moveTrack}
                        trackUrl={trackUrl}
                        setTrackUrl={setTrackUrl}
                        playCurrentAudio={playCurrentAudio}
                        pauseCurrentAudio={pauseCurrentAudio}
                        getTrackTimestamRepresent={getTrackTimestamRepresent}
                        trackTimespan={trackTimespan}
                        trackType={'day'}
                    />

                    <TrackItem
                        performingAction={performingAction}
                        description={nightDescription}
                        handleDescriptionChange={handleNightDescriptionChange}
                        handleTrackUrlChange={handleNightTrackUrlChange}
                        handleAddTrackFile={handleAddNightTrackFile}
                        trackFile={nightTrackFile}
                        moveTrack={moveTrack}
                        trackUrl={nightTrackUrl}
                        setTrackUrl={setNightTrackUrl}
                        playCurrentAudio={playCurrentNightAudio}
                        pauseCurrentAudio={pauseCurrentNightAudio}
                        getTrackTimestamRepresent={getNightTrackTimestamRepresent}
                        trackTimespan={nightTrackTimespan}
                        trackType={'night'}
                    />

                    <TextField
                        disabled
                        // error={!!(errors && errors.name)}
                        // fullWidth
                        label="Дата последнего ред."
                        required
                        type="text"
                        value={
                            lastEditDate instanceof Date
                                ? dayjs(lastEditDate).format(
                                'DD-MM-YYYY HH:mm:ss'
                                )
                                : 'не указано'
                        }
                        className={classes.textfield}
                        multiline
                        // onKeyDown={(event) => {}}

                        onChange={handleLastEditDateChange}
                    />

                    <InputLabel
                        shrink
                        id="track-status"
                        className={classes.textfield}
                    >
                        Статус трека
                    </InputLabel>
                    <Select
                        value={trackStatus}
                        onChange={handleTrackStatusChange}
                        className={classes.textfield}
                        labelId="track-status"
                    >
                        <MenuItem value="blocked">Заблокирован</MenuItem>
                        <MenuItem value="deleted">Удален</MenuItem>
                        <MenuItem value="free">Бесплатный</MenuItem>
                        <MenuItem value="regular">Обычный</MenuItem>
                        <MenuItem value="vip">VIP</MenuItem>
                    </Select>
                    {/* <TextField
                        hidden
                        disabled
                        // error={!!(errors && errors.name)}
                        // fullWidth
                        label="Автор трека"
                        required
                        type="text"
                        value={
                            typeof trackCreator === 'string' &&
                            trackCreator.length > 0
                                ? trackCreator
                                : 'не указано'
                        }
                        className={classes.textfield}
                        multiline
                        // onKeyDown={(event) => {}}
                        onChange={handleTrackCreatorChange}
                    />
                    <TextField
                        hidden
                        disabled
                        // error={!!(errors && errors.name)}
                        // fullWidth
                        label="Последний редактор"
                        required
                        type="text"
                        value={
                            typeof lastEditAuthor === 'string' &&
                            lastEditAuthor.length > 0
                                ? lastEditAuthor
                                : 'не указано'
                        }
                        className={classes.textfield}
                        multiline
                        // onKeyDown={(event) => {}}
                        onChange={handleLastEditAuthorChange}
                    /> */}
                    <div className={classes.iconsWrapper}>
                        <IconButton
                            edge="end"
                            onClick={handleSave}
                            disabled={!changed}
                        >
                            <SaveIcon/>
                        </IconButton>
                        <IconButton edge="end" onClick={handleOpenPushCreate}>
                            <NotificationsIcon/>
                        </IconButton>
                        <IconButton edge="end" onClick={openAlertDialog}>
                            <DeleteIcon/>
                        </IconButton>
                    </div>
                    <AlertDialog
                        title={'Удаление трека'}
                        text={'Вы уверены, что хотите удалить трек?'}
                        open={alertDialog}
                        handleClose={closeAlertDialog}
                    />
                    <Dialog
                        open={pushCreate}
                        onClose={handleClosePushCreate}
                        maxWidth="sm"
                        fullWidth
                    >
                        {track.data.lastPushDateTime !== undefined && (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                className={classes.pushLastTime}
                            >
                                *Дата последнего уведомления:{' '}
                                {dayjs(track.data.lastPushDateTime).format(
                                    'DD/MM/YYYY HH:mm:ss'
                                )}
                            </Typography>
                        )}
                        <div className={classes.pushWrapper}>
                            <PushCreate
                                data={{path: track.ref.path}}
                                onSubmit={handleClosePushCreate}
                            />
                        </div>
                    </Dialog>
                </>
            )}
        </div>
    )
}

const useStyles = makeStyles((theme) => ({
    tabsWrapper: {
        flexGrow: 1,
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
    },
    textfield: {
        width: '300px',
        marginTop: '5px',
    },
    tabContentWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        paddingLeft: '15px',
        paddingRight: '15px',
    },
    iconsWrapper: {
        display: 'flex',
        alignItems: 'center',
    },
    auraColor: {
        marginTop: '10px',
    },
    popover: {
        position: 'absolute',
        zIndex: '2',
    },
    cover: {
        position: 'fixed',
        top: '0px',
        right: '0px',
        bottom: '0px',
        left: '0px',
    },
    colorIndicator: {
        cursor: 'pointer',
        display: 'block',
        width: '30px',
        height: '30px',
        borderRadius: '50%',
        backgroundColor: (props) => props.auraColor,
    },
    auraColorWrapper: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    input: {
        display: 'none',
    },
    uploadButton: {
        marginTop: '5px',
        marginRight: '5px',
        marginBottom: '5px',
    },
    pushWrapper: {
        padding: 15,
    },
    pushLastTime: {
        margin: 5,
        marginBottom: 0,
    },
}))
